import React, { useState } from 'react';
import { ZapIcon, DatabaseIcon, GitBranchIcon, SendIcon, ClockIcon, BarChart3Icon, PlayIcon, SaveIcon, XIcon, PlusIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
export function OrchestrationEditorPage() {
  const [selectedNode, setSelectedNode] = useState<string>('export-salesloft');
  const [isDraft, setIsDraft] = useState(true);
  const nodeTypes = [{
    category: 'Trigger',
    nodes: [{
      id: 'signal-threshold',
      label: 'Signal Threshold',
      icon: ZapIcon
    }, {
      id: 'segment-entry',
      label: 'Segment Entry',
      icon: DatabaseIcon
    }, {
      id: 'webhook-in',
      label: 'Webhook In',
      icon: SendIcon
    }, {
      id: 'schedule',
      label: 'Schedule',
      icon: ClockIcon
    }]
  }, {
    category: 'Data',
    nodes: [{
      id: 'discover-contacts',
      label: 'Discover ZoomInfo Contacts',
      icon: DatabaseIcon
    }, {
      id: 'append-firmographics',
      label: 'Append Firmographics',
      icon: DatabaseIcon
    }, {
      id: 'enrich-technographics',
      label: 'Enrich Technographics',
      icon: DatabaseIcon
    }, {
      id: 'validate-emails',
      label: 'Validate Emails',
      icon: CheckCircleIcon
    }]
  }, {
    category: 'Decision',
    nodes: [{
      id: 'if-else',
      label: 'If/Else',
      icon: GitBranchIcon
    }, {
      id: 'switch',
      label: 'Switch by Attribute',
      icon: GitBranchIcon
    }, {
      id: 'ab-split',
      label: 'A/B Split',
      icon: GitBranchIcon
    }]
  }, {
    category: 'Action',
    nodes: [{
      id: 'export-salesforce',
      label: 'Export to Salesforce',
      icon: SendIcon
    }, {
      id: 'export-salesloft',
      label: 'Export to Salesloft',
      icon: SendIcon
    }, {
      id: 'export-hubspot',
      label: 'Export to HubSpot',
      icon: SendIcon
    }, {
      id: 'add-cadence',
      label: 'Add to Cadence',
      icon: SendIcon
    }, {
      id: 'assign-user',
      label: 'Assign User',
      icon: SendIcon
    }, {
      id: 'webhook-out',
      label: 'Webhook Out',
      icon: SendIcon
    }]
  }, {
    category: 'Control',
    nodes: [{
      id: 'wait-until',
      label: 'Wait Until',
      icon: ClockIcon
    }, {
      id: 'timeout',
      label: 'Timeout',
      icon: ClockIcon
    }]
  }, {
    category: 'Measurement',
    nodes: [{
      id: 'log-metric',
      label: 'Log Metric',
      icon: BarChart3Icon
    }, {
      id: 'increment-counter',
      label: 'Increment Counter',
      icon: BarChart3Icon
    }]
  }];
  const flowNodes = [{
    id: 'signals',
    type: 'trigger',
    label: 'Signals',
    icon: ZapIcon,
    status: 'complete',
    config: {
      source: 'Intent signals',
      threshold: '3+ buying signals',
      window: '7 days'
    }
  }, {
    id: 'discover-contacts',
    type: 'data',
    label: 'Discover ZoomInfo Contacts',
    icon: DatabaseIcon,
    status: 'complete',
    config: {
      provider: 'ZoomInfo',
      fields: ['Email', 'Phone', 'Title'],
      matchConfidence: 'High'
    }
  }, {
    id: 'export-salesloft',
    type: 'action',
    label: 'Export to Salesloft',
    icon: SendIcon,
    status: 'warning',
    config: {
      updateExisting: true,
      createNew: true,
      recordType: 'accounts-people',
      cadence: 'Enterprise Outbound Q1',
      assignUsers: ['Sarah Chen', 'Mike Rodriguez', 'Lisa Park']
    }
  }, {
    id: 'send-signal',
    type: 'action',
    label: 'Send Signal to Salesloft Rhythm',
    icon: SendIcon,
    status: 'pending',
    config: {
      signalType: 'High Intent',
      priority: 'Urgent'
    }
  }];
  const getNodeStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'border-green-300 bg-green-50';
      case 'warning':
        return 'border-yellow-300 bg-yellow-50';
      case 'error':
        return 'border-red-300 bg-red-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };
  return <div className="w-full h-screen flex flex-col bg-gray-50" data-id="element-5131">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-5132">
        <div className="flex items-center justify-between" data-id="element-5133">
          <div className="flex items-center gap-4" data-id="element-5134">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5135">
              Orchestration Editor
            </h1>
            {isDraft && <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full" data-id="element-5136">
                Draft
              </span>}
          </div>
          <div className="flex items-center gap-3" data-id="element-5137">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5138">
              <PlayIcon className="w-4 h-4" data-id="element-5139" />
              Simulate
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5140">
              <SaveIcon className="w-4 h-4" data-id="element-5141" />
              Save Draft
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-5142">
              <CheckCircleIcon className="w-4 h-4" data-id="element-5143" />
              Publish
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-5144">
        {/* Left Sidebar - Node Palette */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto" data-id="element-5145">
          <div className="p-4" data-id="element-5146">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-5147">
              Node Palette
            </h3>
            <div className="space-y-4" data-id="element-5148">
              {nodeTypes.map(category => <div key={category.category} data-id="element-5149">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-5150">
                    {category.category}
                  </h4>
                  <div className="space-y-1" data-id="element-5151">
                    {category.nodes.map(node => {
                  const Icon = node.icon;
                  return <button key={node.id} className="w-full flex items-center gap-2 p-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors" data-id="element-5152">
                          <Icon className="w-4 h-4 text-gray-500" data-id="element-5153" />
                          {node.label}
                        </button>;
                })}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
        {/* Center Canvas */}
        <div className="flex-1 overflow-y-auto p-8" data-id="element-5154">
          <div className="max-w-2xl mx-auto space-y-4" data-id="element-5155">
            {flowNodes.map((node, index) => {
            const Icon = node.icon;
            return <div key={node.id} data-id="element-5156">
                  <div onClick={() => setSelectedNode(node.id)} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedNode === node.id ? 'border-blue-500 shadow-lg' : getNodeStatusColor(node.status)}`} data-id="element-5157">
                    <div className="flex items-center gap-3" data-id="element-5158">
                      <div className="w-10 h-10 bg-white rounded-lg border border-gray-300 flex items-center justify-center" data-id="element-5159">
                        <Icon className="w-5 h-5 text-gray-700" data-id="element-5160" />
                      </div>
                      <div className="flex-1" data-id="element-5161">
                        <h4 className="font-semibold text-gray-900" data-id="element-5162">
                          {node.label}
                        </h4>
                        <p className="text-xs text-gray-600 capitalize" data-id="element-5163">
                          {node.type}
                        </p>
                      </div>
                      {node.status === 'warning' && <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium" data-id="element-5164">
                          <InfoIcon className="w-3 h-3" data-id="element-5165" />
                          Validation
                        </div>}
                      {node.status === 'complete' && <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-5166" />}
                    </div>
                  </div>
                  {index < flowNodes.length - 1 && <div className="flex justify-center py-2" data-id="element-5167">
                      <div className="w-0.5 h-8 bg-gray-300" data-id="element-5168" />
                    </div>}
                </div>;
          })}
            <button className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2" data-id="element-5169">
              <PlusIcon className="w-5 h-5" data-id="element-5170" />
              Add Node
            </button>
          </div>
        </div>
        {/* Right Sidebar - Properties Panel */}
        <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto" data-id="element-5171">
          {selectedNode === 'export-salesloft' ? <div className="p-6" data-id="element-5172">
              <div className="flex items-center gap-3 mb-6" data-id="element-5173">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-5174">
                  <SendIcon className="w-5 h-5 text-blue-600" data-id="element-5175" />
                </div>
                <div data-id="element-5176">
                  <h3 className="font-semibold text-gray-900" data-id="element-5177">
                    Export to Salesloft
                  </h3>
                  <p className="text-xs text-gray-600" data-id="element-5178">Action node</p>
                </div>
              </div>
              <div className="space-y-6" data-id="element-5179">
                {/* Update Existing Records */}
                <div data-id="element-5180">
                  <div className="flex items-center justify-between mb-2" data-id="element-5181">
                    <label className="text-sm font-medium text-gray-900" data-id="element-5182">
                      Update Existing Records
                    </label>
                    <div className="flex items-center gap-2" data-id="element-5183">
                      <InfoIcon className="w-4 h-4 text-gray-400" data-id="element-5184" />
                      <input type="checkbox" defaultChecked className="w-10 h-5 bg-blue-600 rounded-full appearance-none cursor-pointer relative
                          before:absolute before:w-4 before:h-4 before:bg-white before:rounded-full before:top-0.5 before:left-0.5
                          checked:before:left-5 before:transition-all" data-id="element-5185" />
                    </div>
                  </div>
                </div>
                {/* Create New Records */}
                <div data-id="element-5186">
                  <label className="text-sm font-medium text-gray-900 block mb-2" data-id="element-5187">
                    Create New Records
                  </label>
                </div>
                {/* Record Type */}
                <div data-id="element-5188">
                  <label className="text-sm font-medium text-gray-900 block mb-2" data-id="element-5189">
                    Record Type
                  </label>
                  <div className="space-y-2" data-id="element-5190">
                    <label className="flex items-center gap-2" data-id="element-5191">
                      <input type="radio" name="recordType" className="w-4 h-4 text-blue-600" data-id="element-5192" />
                      <span className="text-sm text-gray-700" data-id="element-5193">Accounts</span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-5194">
                      <input type="radio" name="recordType" className="w-4 h-4 text-blue-600" data-id="element-5195" />
                      <span className="text-sm text-gray-700" data-id="element-5196">People</span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-5197">
                      <input type="radio" name="recordType" defaultChecked className="w-4 h-4 text-blue-600" data-id="element-5198" />
                      <span className="text-sm text-gray-700" data-id="element-5199">
                        Accounts & People
                      </span>
                    </label>
                  </div>
                </div>
                {/* Cadence */}
                <div data-id="element-5200">
                  <label className="text-sm font-medium text-gray-900 block mb-2" data-id="element-5201">
                    Cadence
                  </label>
                  <div className="flex items-center gap-2 mb-2" data-id="element-5202">
                    <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 rounded" data-id="element-5203" />
                    <span className="text-sm text-gray-700" data-id="element-5204">
                      Add to cadence
                    </span>
                  </div>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-5205">
                    <option data-id="element-5206">Select cadence</option>
                    <option selected data-id="element-5207">Enterprise Outbound Q1</option>
                    <option data-id="element-5208">SMB Nurture</option>
                    <option data-id="element-5209">Trial Activation</option>
                  </select>
                </div>
                {/* Assign to Users */}
                <div data-id="element-5210">
                  <label className="text-sm font-medium text-gray-900 block mb-2" data-id="element-5211">
                    Assign to user(s)
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" data-id="element-5212">
                    <option data-id="element-5213">Select user(s)</option>
                    <option data-id="element-5214">Sarah Chen</option>
                    <option data-id="element-5215">Mike Rodriguez</option>
                    <option data-id="element-5216">Lisa Park</option>
                  </select>
                  <p className="text-xs text-gray-600" data-id="element-5217">
                    New accounts, and their people, will be rotated among the
                    users selected for assignment.
                  </p>
                </div>
                {/* Export Details Accordion */}
                <div className="border-t border-gray-200 pt-4" data-id="element-5218">
                  <button className="flex items-center justify-between w-full text-sm font-medium text-gray-900" data-id="element-5219">
                    <span data-id="element-5220">Export Details</span>
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-5221">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-5222" />
                    </svg>
                  </button>
                </div>
              </div>
            </div> : <div className="p-6" data-id="element-5223">
              <p className="text-sm text-gray-600" data-id="element-5224">
                Select a node to view and edit its properties
              </p>
            </div>}
        </div>
      </div>
      {/* Footer Info */}
      <div className="bg-white border-t border-gray-200 px-6 py-3" data-id="element-5225">
        <p className="text-xs text-gray-600 text-center" data-id="element-5226">
          A CSV file will be built placing records under management. View/manage
          in the action menu.
        </p>
      </div>
    </div>;
}